'use client';

import React from 'react';
import {
  useCartService,
  useRegionCurrency,
  CLASSNAME,
  buildTiersFromProgressBar,
  buildMilestonesFromTiers,
  calculateProgress,
  getAnchorsForTierCount,
  getNextMilestoneIndex,
  formatBmsmCurrency,
} from '@skin-seoul/service';
import { CheckIcon } from './components/CheckIcon';
import { ConfettiAnimation } from './components/ConfettiAnimation';
import { BMSMGradient } from './components/BMSMGradient';
import { FreeShippingOnlyBanner } from './FreeShippingOnlyBanner';

interface BMSMCampaignBannerProps {
  className?: string;
  /** Banner height in px */
  height?: number;
}

export function BMSMCampaignBanner({ className = '', height = 116 }: BMSMCampaignBannerProps) {
  const { currency } = useRegionCurrency();
  const { useGetCartSuspenseQuery } = useCartService();

  const { data: cartData } = useGetCartSuspenseQuery({ currency });

  const cartTotalPrice = cartData?.cart.totals.totalPrice ?? 0;
  const progressBar = cartData?.cart.progressBar ?? null;

  if (!progressBar) return null;

  const tiers = buildTiersFromProgressBar({
    freeShipping: progressBar.freeShipping,
    discountTiers: progressBar.discountTiers,
  });

  const milestones = buildMilestonesFromTiers({
    tiers,
    progressBar,
    cartTotalPrice,
    currency,
  });

  const progressTiers = [
    ...(progressBar.freeShipping ? [progressBar.freeShipping] : []),
    ...[...progressBar.discountTiers].sort((a, b) => a.displayOrder - b.displayOrder),
  ];
  const anchors = getAnchorsForTierCount(progressTiers.length);
  const progress = calculateProgress({ tiers: progressTiers, anchors });

  const showFreeShippingConfetti = milestones[0]?.achieved ?? false;
  const showDiscountConfetti = milestones.find((m) => m.tier.type === 'discount')?.achieved ?? false;

  const nextMilestoneIndex = getNextMilestoneIndex(milestones);

  const isFreeShippingOnly = tiers.length === 1 && tiers[0]?.type === 'shipping';

  if (isFreeShippingOnly && progressBar.freeShipping) {
    const milestone = milestones[0];
    const { minAmount, remaining } = progressBar.freeShipping;
    return (
      <FreeShippingOnlyBanner
        currency={currency}
        minAmount={minAmount}
        remaining={Math.max(0, remaining)}
        applied={milestone?.achieved ?? false}
        progress={progress}
        className={className}
        height={height}
      />
    );
  }

  const LABEL_AREA_HEIGHT_PX = 56;
  const ICON_SIZE_PX = 24;
  const BAR_HEIGHT_PX = 4;
  const CHECK_TEXT_GAP_PX = 4;

  return (
    <div
      className={`relative w-full flex flex-col bg-generic-100 overflow-hidden items-center justify-center py-4 gap-2 rounded-2xl ${className}`}
      style={{ minHeight: `${height}px`, height: `${height}px` }}
    >
      <BMSMGradient />

      <div
        className={`z-[1] relative w-full px-4 flex items-center justify-center ${CLASSNAME.NOT_TRANSLATE}`}
        style={{ height: `${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px` }}
      >
        <div className="absolute left-0 right-0 bottom-0" style={{ height: `${ICON_SIZE_PX}px` }}>
          {/* Progress Bar */}
          <div
            className="absolute left-4 right-4 bg-neutral-600 rounded-sm"
            style={{ top: `${(ICON_SIZE_PX - BAR_HEIGHT_PX) / 2}px`, height: `${BAR_HEIGHT_PX}px` }}
          >
            <div
              className="h-full bg-success-600 rounded-sm transition-[width] duration-300 ease-in-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Milestones */}
          <div
            className="absolute left-4 right-4"
            style={{
              top: `-${LABEL_AREA_HEIGHT_PX + CHECK_TEXT_GAP_PX}px`,
              height: `${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px`,
            }}
          >
            {milestones.map((m, index) => {
              const clamped = Math.max(0, Math.min(1, m.anchorPercent));

              const left = (() => {
                if (clamped === 0) return '0%';
                if (clamped === 1) return '100%';
                return `${clamped * 100}%`;
              })();

              const translate = (() => {
                if (clamped === 0) return 'translateX(0)';
                if (clamped === 1) return 'translateX(-100%)';
                return 'translateX(-50%)';
              })();

              const iconSize = m.displayConfig.iconVariant === 'dot' ? '12px' : '24px';
              const iconBottom = m.displayConfig.iconVariant === 'dot' ? '6px' : '0';

              const isSupersededByHigherTier =
                m.achieved &&
                milestones.slice(index + 1).some((other) => other.achieved && other.tier.type === m.tier.type);
              const textOpacity = (m.achieved && !m.displayConfig.active) || isSupersededByHigherTier ? 0.2 : 1;

              // Product decision: never show savings label for shipping tiers.
              const showSavings = m.tier.type === 'discount' && nextMilestoneIndex === index && m.savingsAmount > 0;
              const savingsVisibility = showSavings ? 'visible' : 'hidden';

              return (
                <React.Fragment key={`${m.tier.type}-${index}`}>
                  <div
                    className="absolute flex flex-col gap-[2px] items-center"
                    style={{
                      left,
                      transform: translate,
                      bottom: `${ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px`,
                      opacity: textOpacity,
                      maxWidth: `${Math.floor(100 / milestones.length)}%`,
                    }}
                  >
                    <span
                      className={`text-label-xsmall whitespace-nowrap overflow-hidden text-ellipsis max-w-full font-medium ${m.achieved ? 'text-primary-200' : 'text-neutral-400'}`}
                    >
                      {m.achieved ? m.statusText : `Spend ${m.statusText}`}
                    </span>
                    <span
                      className="text-label-large whitespace-nowrap font-bold text-generic-0"
                      style={{ letterSpacing: '-0.64px' }}
                    >
                      {m.title}
                    </span>
                    <span
                      className="text-label-small whitespace-nowrap font-semibold text-[#FFFF27]"
                      style={{ visibility: savingsVisibility as 'visible' | 'hidden' }}
                    >
                      Save {formatBmsmCurrency(currency, m.savingsAmount)}
                    </span>
                  </div>
                  <div
                    className="absolute"
                    style={{
                      left,
                      transform: translate,
                      bottom: iconBottom,
                      width: iconSize,
                      height: iconSize,
                      opacity: textOpacity,
                    }}
                  >
                    <CheckIcon achieved={m.achieved} variant={m.displayConfig.iconVariant} />
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <ConfettiAnimation
        showFreeShippingConfetti={showFreeShippingConfetti}
        showDiscountConfetti={showDiscountConfetti}
      />
    </div>
  );
}
